import { QRCodeSVG } from 'qrcode.react';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

/**
 * A secret and its otpauth:// URL, rendered for a phone to consume.
 *
 * Shared between two screens that both hit this exact moment for different
 * reasons - MfaSettings when somebody enrols themselves, MfaForm's setup step
 * when an account was provisioned with MFA already on. Same bytes, same
 * problem, so one component rather than two copies drifting apart.
 *
 * Rendered locally rather than fetched as an image: the otpauth:// URL
 * contains the secret, so turning it into a picture via a remote service would
 * hand the one credential this feature protects to a third party. `qrcode.react`
 * draws the code from an SVG generated in the browser - the secret never
 * leaves it.
 */
export function AuthenticatorSetup({
  secret,
  otpauthUrl,
  lostSecretHint,
}: {
  secret: string;
  otpauthUrl: string;
  /** Recovery differs by caller: self-service can restart, a provisioned account cannot. */
  lostSecretHint: string;
}) {
  return (
    <div className="space-y-4">
      <div className="flex justify-center rounded-md border border-border bg-white p-4">
        <QRCodeSVG value={otpauthUrl} size={168} marginSize={2} />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="mfa-secret">Or type this in</Label>
        <div className="flex gap-2">
          <Input
            id="mfa-secret"
            readOnly
            value={secret}
            className="font-mono text-xs"
            onFocus={(event) => {
              event.target.select();
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="Copy the secret"
            onClick={() => {
              void navigator.clipboard.writeText(secret).then(
                () => {
                  toast.success('Copied');
                },
                () => {
                  // Clipboard access is refused in some contexts. The field is
                  // selectable, so this is inconvenient rather than blocking.
                  toast.error('Could not copy - select the text instead');
                },
              );
            }}
          >
            <Copy className="size-4" />
          </Button>
        </div>
        {/*
          Said plainly. The server keeps only a sealed copy, so there is no way
          to show this again once it is confirmed.
        */}
        <p className="text-xs text-muted-foreground">{lostSecretHint}</p>
      </div>
    </div>
  );
}
